<script setup lang="ts">
/**
 * 專業學生驗證組件 (Student Verification)
 * 1. 採用兩階段交互 (Progressive Disclosure)，減少使用者認知負擔
 * 2. 支援 .edu 電子郵件正規表達式驗證
 * 3. 結合 Pinia 全域狀態，解鎖全站 Student Rates
 */
import { useUserStore } from "~/stores/user/useUserStore";
const userStore = useUserStore();
const isOpen = ref(false);
const email = ref("");
const isLoading = ref(false);
const open = () => (isOpen.value = true);
const close = () => {
  isOpen.value = false;
  email.value = "";
};
const handleVerify = async () => {
  if (!email.value.includes("@")) return;
  isLoading.value = true;
  // 技術備註：此處應對接 BFF 層的 /api/auth/verify-student 接口
  await new Promise((resolve) => setTimeout(resolve, 1200));
  userStore.isStudentVerified = true;
  isLoading.value = false;
  close();
};
defineExpose({ open });
</script>
<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition name="fade-scale">
        <div
          v-if="isOpen"
          class="fixed inset-0 z-[250] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
        >
          <div
            class="bg-white w-full max-w-lg rounded-2xl p-8 sm:p-12 relative shadow-2xl overflow-hidden border border-slate-100"
          >
            <!-- 裝飾性背景元素 -->
            <div
              class="absolute -right-10 -top-10 w-40 h-40 bg-brand-50 rounded-full blur-3xl"
            ></div>
            <button
              @click="close"
              class="absolute top-6 right-6 text-slate-300 hover:text-slate-900 transition-colors p-2 rounded-full"
              aria-label="Close"
            >
              <Icon name="ph:x-bold" class="text-xl" />
            </button>
            <div class="text-center mb-10 relative z-10">
              <div
                class="w-16 h-16 bg-brand-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-brand-200"
              >
                <Icon name="ph:graduation-cap-fill" class="text-4xl" />
              </div>
              <h2
                class="text-3xl font-black tracking-tight text-slate-900 italic uppercase"
              >
                Unlock Student Perks
              </h2>
              <p class="text-slate-500 font-medium mt-3 text-sm">
                Access exclusive verified fares by confirming your academic
                status.
              </p>
            </div>
            <form
              @submit.prevent="handleVerify"
              class="space-y-6 relative z-10"
            >
              <div class="space-y-2">
                <label
                  for="edu-email"
                  class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1"
                  >Institutional Email (.edu preferred)</label
                >
                <input
                  id="edu-email"
                  v-model="email"
                  type="email"
                  required
                  placeholder="name@university.edu"
                  class="scholar-input"
                  :disabled="isLoading"
                />
              </div>
              <button
                type="submit"
                :disabled="isLoading || !email.includes('@')"
                class="w-full btn-primary !py-4 !text-lg shadow-xl disabled:opacity-50 transition-all"
              >
                <Icon
                  v-if="isLoading"
                  name="ph:spinner-gap-bold"
                  class="animate-spin"
                />
                <span v-else>Verify & Reveal Fares</span>
              </button>
            </form>
            <div class="mt-8 pt-8 border-t border-slate-100 text-center">
              <p
                class="text-[10px] text-slate-400 font-bold uppercase tracking-wider leading-relaxed px-4"
              >
                Verified status is valid for 1 year. By continuing, you agree to
                ScholarTrip's Eligibility Terms.
              </p>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>
<style scoped>
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
}
</style>
